import React, { useState, useEffect, useCallback } from "react";
import { useAppSelector } from "../../../../store/hooks";
import { formatNumber } from "../../../../utils/formatting";
import { calculateFuelRequirements, calculateJumpFuelConsumption, calculatePowerPlantFuelConsumption } from "../../../../utils/calculations/fuelCalculations";

interface BreakawayFuelConfigProps {
  isOpen: boolean;
  onClose: () => void;
  inline?: boolean; // If true, render inline instead of as modal
}

interface SectionFuel {
  sectionId: string;
  fuelPercentage: number;
  fuelTons: number;
  powerPlantFuel: number;
  jumpDriveFuel: number;
  reactionDriveFuel: number;
  totalRequired: number;
  isAdequate: boolean;
}

export const BreakawayFuelConfig: React.FC<BreakawayFuelConfigProps> = ({
  isOpen,
  onClose,
  inline = false,
}) => {
  const breakawayHull = useAppSelector((state) => state.breakawayHull);
  const fuelTanks = useAppSelector((state) => state.shipBuilder.fuelTanks) || 0;
  const drives = useAppSelector((state) => state.shipBuilder.drives);
  const powerPlant = useAppSelector((state) => state.shipBuilder.powerPlant);
  const shipSize = useAppSelector((state) => state.shipBuilder.shipSize);
  
  const [sectionFuels, setSectionFuels] = useState<Record<string, SectionFuel>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [configMode, setConfigMode] = useState<'percentage' | 'manual'>('percentage');

  const calculateCombinedFuelRequirements = useCallback(() => {
    // Calculate fuel requirements for the combined ship (when sections are together)
    // This follows Traveller rules: "drives, power plants and weapons can all be combined when calculating performance"
    const weeks = 4; // Default to 4 weeks for fuel calculations
    const requirements = calculateFuelRequirements(drives, powerPlant, shipSize, weeks);
    
    return {
      powerPlantFuel: requirements.powerPlant,
      jumpDriveFuel: requirements.jumpDrive,
      reactionDriveFuel: requirements.reactionDrive,
      totalRequired: requirements.total,
    };
  }, [drives, powerPlant, shipSize]);

  const calculateSectionFuelRequirements = useCallback((sectionId: string, sectionSize: number) => {
    // Calculate fuel requirements for this specific section based on its actual drives and power plant
    const weeks = 4; // Default to 4 weeks for fuel calculations
    
    // Get drives assigned to this specific section
    const sectionDrives = drives.filter(drive => drive.section === parseInt(sectionId.split('-')[1]));
    
    // For breakaway hulls, each section should have its own power plant
    // For now, we'll estimate the section's power plant based on its size ratio
    // This should be updated when power plant distribution is implemented
    const sectionRatio = sectionSize / shipSize;
    const sectionPowerPlant = powerPlant ? {
      ...powerPlant,
      weight: Math.ceil(powerPlant.weight * sectionRatio),
      power: Math.ceil(powerPlant.power * sectionRatio),
    } : null;
    
    // Calculate fuel requirements manually for this section to avoid using shipTons
    let reactionDrive = 0;
    let jumpDrive = 0;
    let powerPlantFuel = 0;
    
    // Calculate reaction drive fuel for this section only
    const reactionDrives = sectionDrives.filter((d) => d.type === "reaction");
    reactionDrives.forEach((drive) => {
      const hoursPerWeek = 168; // 7 days × 24 hours
      const totalHours = weeks * hoursPerWeek;
      if (drive.rating === 0) {
        reactionDrive += 0.25 * totalHours; // 0.25 tons per hour for thrust 0
      } else {
        // 2.5% per thrust per hour of section's tonnage (not ship's tonnage)
        reactionDrive += sectionSize * 0.025 * drive.rating * totalHours;
      }
    });
    
    // Calculate jump drive fuel for this section only
    const jumpDrives = sectionDrives.filter((d) => d.type === "jump");
    jumpDrives.forEach((drive) => {
      // 10% of section's tonnage × jump rating (not ship's tonnage)
      jumpDrive += calculateJumpFuelConsumption(drive.rating, sectionSize);
    });
    
    // Calculate power plant fuel for this section's power plant
    if (sectionPowerPlant) {
      powerPlantFuel = calculatePowerPlantFuelConsumption(sectionPowerPlant, weeks);
    }
    
    const total = reactionDrive + jumpDrive + powerPlantFuel;
    
    // Debug logging
    if (process.env.NODE_ENV === 'development') {
      console.log(`Section ${sectionId} fuel calculation:`, {
        sectionSize,
        sectionDrives: sectionDrives.map(d => ({ type: d.type, rating: d.rating, section: d.section })),
        sectionPowerPlant: sectionPowerPlant ? { weight: sectionPowerPlant.weight, power: sectionPowerPlant.power } : null,
        requirements: { reactionDrive, jumpDrive, powerPlant: powerPlantFuel, total }
      });
    }
    
    return {
      powerPlantFuel,
      jumpDriveFuel: jumpDrive,
      reactionDriveFuel: reactionDrive,
      totalRequired: total,
    };
  }, [drives, powerPlant, shipSize]);

  // Initialize section fuel allocations
  useEffect(() => {
    if (!breakawayHull?.sections) return;
    
    const sections = breakawayHull.sections;
    const newSectionFuels: Record<string, SectionFuel> = {};

    sections.forEach(section => {
      const totalSize = sections.reduce((sum, s) => sum + s.size, 0);
      const sectionRatio = section.size / totalSize;
      const sectionFuelTons = Math.ceil(fuelTanks * sectionRatio);
      
      // Calculate fuel requirements for this specific section
      const sectionRequirements = calculateSectionFuelRequirements(section.id, section.size);

      newSectionFuels[section.id] = {
        sectionId: section.id,
        fuelPercentage: Math.round(sectionRatio * 100),
        fuelTons: sectionFuelTons,
        powerPlantFuel: sectionRequirements.powerPlantFuel,
        jumpDriveFuel: sectionRequirements.jumpDriveFuel,
        reactionDriveFuel: sectionRequirements.reactionDriveFuel,
        totalRequired: sectionRequirements.totalRequired,
        isAdequate: sectionFuelTons >= sectionRequirements.totalRequired,
      };
    });

    setSectionFuels(newSectionFuels);
  }, [breakawayHull?.sections, fuelTanks, calculateSectionFuelRequirements, calculateCombinedFuelRequirements]);

  const handleFuelPercentageChange = (sectionId: string, percentage: number) => {
    if (!breakawayHull?.sections) return;

    const section = breakawayHull.sections.find(s => s.id === sectionId);
    if (!section) return;

    const sectionRequirements = calculateSectionFuelRequirements(sectionId, section.size);
    
    const fuelTons = (fuelTanks * percentage) / 100;
    const isAdequate = fuelTons >= sectionRequirements.totalRequired;

    setSectionFuels(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        sectionId,
        fuelPercentage: percentage,
        fuelTons,
        powerPlantFuel: sectionRequirements.powerPlantFuel,
        jumpDriveFuel: sectionRequirements.jumpDriveFuel,
        reactionDriveFuel: sectionRequirements.reactionDriveFuel,
        totalRequired: sectionRequirements.totalRequired,
        isAdequate,
      }
    }));

    // Clear error for this section
    setErrors(prev => ({ ...prev, [sectionId]: '' }));

    // Auto-save in inline mode
    if (inline) {
      // Use setTimeout to ensure state updates are processed first
      setTimeout(() => {
        const hasErrors = Object.values(errors).some(error => error !== '');
        if (!hasErrors) {
          handleSave();
        }
      }, 0);
    }
  };

  const handleProportionalDistribution = () => {
    if (!breakawayHull?.sections) return;

    const totalShipSize = breakawayHull.sections.reduce((sum, section) => sum + section.size, 0);
    const newSectionFuels: Record<string, SectionFuel> = {};

    breakawayHull.sections.forEach(section => {
      const fuelPercentage = (section.size / totalShipSize) * 100;
      const fuelTons = (fuelTanks * fuelPercentage) / 100;
      
      // Calculate fuel requirements for this specific section
      const sectionRequirements = calculateSectionFuelRequirements(section.id, section.size);
      
      const isAdequate = fuelTons >= sectionRequirements.totalRequired;

      newSectionFuels[section.id] = {
        sectionId: section.id,
        fuelPercentage,
        fuelTons,
        powerPlantFuel: sectionRequirements.powerPlantFuel,
        jumpDriveFuel: sectionRequirements.jumpDriveFuel,
        reactionDriveFuel: sectionRequirements.reactionDriveFuel,
        totalRequired: sectionRequirements.totalRequired,
        isAdequate,
      };
    });

    setSectionFuels(newSectionFuels);
    setErrors({});

    // Auto-save in inline mode
    if (inline) {
      // Use setTimeout to ensure state updates are processed first
      setTimeout(() => {
        handleSave();
      }, 0);
    }
  };

  const getTotalFuelAllocated = () => {
    return Object.values(sectionFuels).reduce((sum, section) => sum + section.fuelTons, 0);
  };

  const getTotalPercentage = useCallback(() => {
    return Object.values(sectionFuels).reduce((sum, section) => sum + section.fuelPercentage, 0);
  }, [sectionFuels]);

  const handleSave = useCallback(() => {
    // Check for errors
    const hasErrors = Object.values(errors).some(error => error !== '');
    if (hasErrors) {
      return;
    }

    // Validate total percentage equals 100%
    const totalPercentage = getTotalPercentage();
    if (Math.abs(totalPercentage - 100) > 0.01) {
      setErrors(prev => ({ ...prev, general: `Total percentage must equal 100%. Current: ${totalPercentage.toFixed(1)}%` }));
      return;
    }

    // Validate all sections have adequate fuel
    const inadequateSections = Object.values(sectionFuels).filter(section => !section.isAdequate);
    if (inadequateSections.length > 0) {
      setErrors(prev => ({ 
        ...prev, 
        general: `Some sections have insufficient fuel for their requirements.` 
      }));
      return;
    }

    // For now, we'll just close the modal since the main fuel tanks are already set
    // In a more complex implementation, you might want to store section-specific fuel data
    if (!inline) {
      onClose();
    }
  }, [inline, onClose, errors, sectionFuels, getTotalPercentage]);

  if (!isOpen || !breakawayHull?.sections) return null;

  const totalFuelAllocated = getTotalFuelAllocated();
  const totalPercentage = getTotalPercentage();

  const content = (
    <div className={inline ? "w-full" : "bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"}>
      {!inline && (
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Breakaway Fuel Configuration
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

        <div className="mb-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Breakaway Fuel Rules</h3>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• When sections are together, drives and power plants are combined for performance calculations</li>
              <li>• Each breakaway section can be assigned a percentage of total fuel reserves</li>
              <li>• Each section must have enough fuel to power its proportional share of the combined ship's systems</li>
              <li>• Fuel requirements are calculated based on the combined ship's drives and power plant, then distributed proportionally</li>
              <li>• Total percentage must equal 100%</li>
              <li>• This ensures each section can operate independently when separated</li>
            </ul>
          </div>
        </div>

        {/* Configuration Mode Selection */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Configuration Mode</h3>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="percentage"
                checked={configMode === 'percentage'}
                onChange={(e) => setConfigMode(e.target.value as 'percentage' | 'manual')}
                className="mr-2"
              />
              <span className="text-sm">Percentage Distribution</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="manual"
                checked={configMode === 'manual'}
                onChange={(e) => setConfigMode(e.target.value as 'percentage' | 'manual')}
                className="mr-2"
              />
              <span className="text-sm">Manual Distribution</span>
            </label>
          </div>
        </div>

        {/* Proportional Distribution Button */}
        <div className="mb-4">
          <button
            onClick={handleProportionalDistribution}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            Distribute Proportionally by Section Size
          </button>
        </div>

        {/* Section Fuel Configuration */}
        <div className="space-y-6">
          {breakawayHull.sections.map((section) => {
            const sectionFuel = sectionFuels[section.id] || {
              sectionId: section.id,
              fuelPercentage: 0,
              fuelTons: 0,
              powerPlantFuel: 0,
              jumpDriveFuel: 0,
              reactionDriveFuel: 0,
              totalRequired: 0,
              isAdequate: false,
            };
            const error = errors[section.id];

            return (
              <div key={section.id} className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">
                  {section.name} ({section.size} tons)
                </h4>

                {error && (
                  <div className="text-red-600 text-sm mb-3 p-2 bg-red-50 border border-red-200 rounded">
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Fuel Percentage */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fuel Percentage
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={sectionFuel.fuelPercentage.toFixed(1)}
                      onChange={(e) => handleFuelPercentageChange(section.id, parseFloat(e.target.value) || 0)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  {/* Fuel Tons */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fuel Tons
                    </label>
                    <div className="text-sm text-gray-900 font-medium">
                      {formatNumber(sectionFuel.fuelTons)} tons
                    </div>
                  </div>
                </div>

                {/* Fuel Requirements */}
                <div className="mt-3 text-sm text-gray-600">
                  <div className="mb-2">
                    <strong>Fuel Requirements:</strong>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>Power Plant: {formatNumber(sectionFuel.powerPlantFuel)} tons</div>
                    <div>Jump Drive: {formatNumber(sectionFuel.jumpDriveFuel)} tons</div>
                    <div>Reaction Drive: {formatNumber(sectionFuel.reactionDriveFuel)} tons</div>
                    <div><strong>Total Required: {formatNumber(sectionFuel.totalRequired)} tons</strong></div>
                  </div>
                </div>

                {/* Adequacy Status */}
                <div className={`mt-2 text-sm font-medium ${sectionFuel.isAdequate ? 'text-green-600' : 'text-red-600'}`}>
                  {sectionFuel.isAdequate ? '✓ Fuel adequate for section requirements' : '✗ Insufficient fuel for section requirements'}
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Summary</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Total Fuel Allocated:</span>
              <span className="ml-2 font-medium">{formatNumber(totalFuelAllocated)} tons</span>
            </div>
            <div>
              <span className="text-gray-600">Total Percentage:</span>
              <span className={`ml-2 font-medium ${Math.abs(totalPercentage - 100) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                {totalPercentage.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>

        {/* General Errors */}
        {errors.general && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-700">{errors.general}</p>
          </div>
        )}

        {!inline && (
          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 mr-3"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={Object.values(errors).some(error => error !== '') || Math.abs(totalPercentage - 100) > 0.01}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save Configuration
            </button>
          </div>
        )}
      </div>
  );

  if (inline) {
    return content;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      {content}
    </div>
  );
}; 